import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { ServiceEnquiryForm, ServiceEnquiryDialog } from "@/components/ServiceEnquiry";
import { getService, resolveServiceSlug } from "@/lib/services";
import { CONTACT, waLink } from "@/lib/brand";
import { ArrowRight, Check, Phone, MessageCircle, ArrowLeft } from "lucide-react";

const consultLink = (s) => `/contact?intent=${s.intent || "buy"}&service=${encodeURIComponent(s.t)}`;
const Eyebrow = ({ children, className = "" }) => <div className={`text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] ${className}`}>{children}</div>;

export default function ServiceDetail() {
    const { slug } = useParams();
    const [open, setOpen] = useState(false);
    const target = resolveServiceSlug(slug);
    if (target !== slug) return <Navigate to={`/services/${target}`} replace/>;
    const s = getService(slug);
    if (!s) return <Navigate to="/services" replace/>;
    const Icon = s.icon;
    const wa = waLink(`Hi EBG Realty, I'd like to know more about your ${s.t} service.`);

    return (
        <PageShell navVariant="dark">
            <section data-testid={`service-hero-${slug}`} className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={s.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/80 to-transparent"/>
                <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 pt-32 pb-16 md:pt-40 md:pb-20">
                    <Link to="/services" data-testid="service-back-top" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[var(--ebg-gold)] mb-6"><ArrowLeft size={12}/> All Services</Link>
                    <Eyebrow className="mb-4 tracking-[0.32em]">{s.eyebrow}</Eyebrow>
                    <div className="flex items-start gap-5">
                        <span className="w-14 h-14 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center shrink-0 mt-1"><Icon size={22} strokeWidth={1.8}/></span>
                        <div>
                            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" data-testid="service-title">{s.title || s.t}</h1>
                            <p className="text-white/75 max-w-2xl mt-5 leading-relaxed text-base md:text-lg">{s.subtitle}</p>
                        </div>
                    </div>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <button onClick={() => setOpen(true)} data-testid="service-hero-enquire" className="bg-[var(--ebg-gold)] text-black px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white transition-colors">Enquire Now</button>
                        <Link to={consultLink(s)} data-testid="service-hero-consult" className="border border-white/40 px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2">Book Consultation <ArrowRight size={12}/></Link>
                        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="service-hero-whatsapp" className="border border-white/20 px-5 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-emerald-600 hover:border-emerald-600 transition-colors inline-flex items-center gap-2"><MessageCircle size={13}/> WhatsApp</a>
                    </div>
                </div>
            </section>

            <section className="bg-[var(--ebg-ivory)]" data-testid="service-overview">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20 grid lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-3">
                        <Eyebrow>Service Overview</Eyebrow>
                        <p className="mt-4 text-black/80 text-lg leading-relaxed font-serif">{s.intro}</p>
                    </div>
                    <div className="lg:col-span-2">
                        <Eyebrow>Why EBG Realty</Eyebrow>
                        <ul className="mt-5 space-y-3.5">
                            {s.benefits.map((b, i) => (
                                <li key={i} data-testid={`service-benefit-${i}`} className="flex items-start gap-3 text-[14.5px] text-black/80 leading-relaxed">
                                    <span className="w-5 h-5 rounded-full bg-[var(--ebg-gold)] flex items-center justify-center mt-0.5 shrink-0"><Check size={12} strokeWidth={3} className="text-black"/></span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {s.offerings?.length > 0 && (
                <section className="bg-white" data-testid="service-offerings">
                    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
                        <Eyebrow>Key Offerings</Eyebrow>
                        <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-[1.06]">What we cover.</h2>
                        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                            {s.offerings.map((o, i) => (
                                <div key={i} className="bg-white p-6" data-testid={`service-offering-${i}`}>
                                    <div className="text-[var(--ebg-gold)] font-serif text-xl">0{i + 1}</div>
                                    <div className="font-serif text-xl mt-2">{o.t}</div>
                                    <p className="text-black/60 text-[13.5px] mt-2 leading-relaxed">{o.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-[#0a0d18] text-white">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
                    <Eyebrow>How it works</Eyebrow>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-[1.06]">A simple, transparent process.</h2>
                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {s.steps.map((step, i) => (
                            <div key={i} data-testid={`service-step-${i}`} className="border border-white/10 p-6 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                                <div className="text-[var(--ebg-gold)] font-serif text-3xl">0{i + 1}</div>
                                <div className="font-serif text-xl mt-3">{step.h}</div>
                                <p className="text-white/60 text-[13.5px] mt-2 leading-relaxed">{step.d}</p>
                            </div>
                        ))}
                    </div>
                    {s.useCases?.length > 0 && (
                        <div className="mt-14" data-testid="service-use-cases">
                            <Eyebrow>Who it&rsquo;s for</Eyebrow>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {s.useCases.map((u) => <span key={u} className="border border-white/15 px-3.5 py-2 text-[12.5px] text-white/80">{u}</span>)}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {s.faqs?.length > 0 && (
                <section className="bg-white">
                    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-20">
                        <Eyebrow>FAQ</Eyebrow>
                        <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-[1.06]">Quick answers.</h2>
                        <div className="mt-8 divide-y divide-black/10 border-y border-black/10">
                            {s.faqs.map((f, i) => (
                                <details key={i} data-testid={`service-faq-${i}`} className="group py-5">
                                    <summary className="font-serif text-lg md:text-xl cursor-pointer list-none flex items-start justify-between gap-6">
                                        {f.q}<span className="text-[var(--ebg-gold)] text-2xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="text-black/65 text-[15px] leading-relaxed mt-3">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-[#0a0d18] text-white border-t border-white/10" data-testid="service-enquire-section">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <Eyebrow>Talk to a specialist</Eyebrow>
                        <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-[1.03]">Ready to explore <em className="not-italic text-[var(--ebg-gold)]">{s.t}?</em></h2>
                        <p className="text-white/70 max-w-md mt-5 leading-relaxed">A senior advisor will call you back within 2 business hours &mdash; no pushy sales.</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to={consultLink(s)} data-testid="service-cta-book" className="inline-flex items-center gap-2 bg-[var(--ebg-gold)] text-black px-7 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white transition-colors">Book Consultation <ArrowRight size={13}/></Link>
                            <a href={CONTACT.tel} data-testid="service-cta-call" className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white hover:text-black transition-colors"><Phone size={13}/> {CONTACT.display}</a>
                            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="service-cta-whatsapp" className="inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-emerald-600 hover:border-emerald-600 transition-colors"><MessageCircle size={13}/> WhatsApp</a>
                        </div>
                        <Link to="/services" data-testid="service-back" className="mt-8 inline-block text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-[var(--ebg-gold)]">&larr; Back to all services</Link>
                    </div>
                    <div className="border border-white/10 bg-white/[0.02] p-6 md:p-8">
                        <div className="font-serif text-2xl mb-5">Enquire Now</div>
                        <ServiceEnquiryForm key={slug} service={s.t} dark testid="service-page-enquiry"/>
                    </div>
                </div>
            </section>
            <ServiceEnquiryDialog service={s.t} open={open} onOpenChange={setOpen}/>
        </PageShell>
    );
}
